import { useRef } from 'react';
import { Crown } from 'lucide-react';
import Avatar from '../Avatar';
import Button from '../ui/Button';
import DayDots from '../lineup/DayDots';
import { groupMembersListSurfaceClass } from '../../config/glassSurfaces';

const SWIPE_THRESHOLD_PX = 48;

/** Tailwind-safe preset widths — must match rendered action buttons. */
function getActionStripClasses(actionKeys) {
  if (actionKeys.length === 0) {
    return { slide: 'translate-x-0', strip: '' };
  }

  const key = [...actionKeys].sort().join(',');

  const layouts = {
    leave: { slide: '-translate-x-[7.5rem]', strip: 'w-[7.5rem]' },
    block: { slide: '-translate-x-[5rem]', strip: 'w-[5rem]' },
    unblock: { slide: '-translate-x-[5.75rem]', strip: 'w-[5.75rem]' },
    'block,promote,remove': { slide: '-translate-x-[16.5rem]', strip: 'w-[16.5rem]' },
    'block,promote': { slide: '-translate-x-[11rem]', strip: 'w-[11rem]' },
    'block,remove': { slide: '-translate-x-[10.5rem]', strip: 'w-[10.5rem]' },
    'promote,remove': { slide: '-translate-x-[11rem]', strip: 'w-[11rem]' },
  };

  return layouts[key] ?? { slide: '-translate-x-[10rem]', strip: 'w-[10rem]' };
}

/**
 * Member rows for the group info modal — swipe left to reveal inline actions.
 *
 * festivalDays/attendanceByUser add the per-member day dots; both are empty for
 * regular-event groups, which render the row exactly as before. Blocked members stay
 * visible-but-dimmed here because this page owns the only Unblock affordance.
 */
export default function GroupMembersList({
  members,
  blockedUsers,
  sessionUserId,
  festivalDays = [],
  attendanceByUser = {},
  viewerAttendingDays = [],
  isManager,
  isDbUuid,
  canLeaveGroup,
  openActionsMemberId,
  onOpenActionsMemberIdChange,
  openActionsRowRef,
  onOpenMemberProfile,
  onMemberPhotoZoom,
  onPromoteMember,
  onRemoveMember,
  onBlockMember,
  onUnblockMember,
  onLeaveGroup,
  formatDate,
}) {
  return (
    <div className={groupMembersListSurfaceClass()}>
      {members.map((member) => (
        // display:contents, so the list's layout is untouched; the first-join moment finds your own row by it
        <div
          key={member.id}
          style={{ display: 'contents' }}
          data-member-self={member.user_id === sessionUserId ? '' : undefined}
        >
          <MemberSwipeRow
            member={member}
            memberProfile={member.profile}
            isCurrentUser={member.user_id === sessionUserId}
            isBlocked={blockedUsers.has(member.user_id)}
            festivalDays={festivalDays}
            attendingDays={attendanceByUser[member.user_id] || []}
            viewerAttendingDays={viewerAttendingDays}
            isOpen={openActionsMemberId === member.id}
            onOpenChange={(open) => onOpenActionsMemberIdChange(open ? member.id : null)}
            rowRef={openActionsMemberId === member.id ? openActionsRowRef : null}
            isManager={isManager}
            isDbUuid={isDbUuid}
            canLeaveGroup={canLeaveGroup}
            onOpenMemberProfile={onOpenMemberProfile}
            onMemberPhotoZoom={onMemberPhotoZoom}
            onPromoteMember={onPromoteMember}
            onRemoveMember={onRemoveMember}
            onBlockMember={onBlockMember}
            onUnblockMember={onUnblockMember}
            onLeaveGroup={onLeaveGroup}
            formatDate={formatDate}
          />
        </div>
      ))}
    </div>
  );
}

function MemberSwipeRow({
  member,
  memberProfile,
  isCurrentUser,
  isBlocked,
  festivalDays,
  attendingDays,
  viewerAttendingDays,
  isOpen,
  onOpenChange,
  rowRef,
  isManager,
  isDbUuid,
  canLeaveGroup,
  onOpenMemberProfile,
  onMemberPhotoZoom,
  onPromoteMember,
  onRemoveMember,
  onBlockMember,
  onUnblockMember,
  onLeaveGroup,
  formatDate,
}) {
  const touchStartRef = useRef({ x: 0, y: 0 });
  const trackingRef = useRef(false);

  const canPromote =
    !isCurrentUser &&
    isManager &&
    isDbUuid(member.id) &&
    member.role !== 'manager' &&
    member.role !== 'creator';
  const canRemove = !isCurrentUser && isManager && isDbUuid(member.id) && member.role !== 'creator';

  const actionKeys = [];
  if (isCurrentUser && canLeaveGroup) {
    actionKeys.push('leave');
  } else if (!isCurrentUser) {
    if (canPromote) actionKeys.push('promote');
    if (canRemove) actionKeys.push('remove');
    if (isBlocked) actionKeys.push('unblock');
    else actionKeys.push('block');
  }

  const { slide, strip } = getActionStripClasses(actionKeys);
  const rowSlideClass = isOpen && actionKeys.length > 0 ? slide : 'translate-x-0';

  const handleTouchStart = (event) => {
    if (actionKeys.length === 0) return;
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    trackingRef.current = true;
  };

  const handleTouchEnd = (event) => {
    if (!trackingRef.current || actionKeys.length === 0) return;
    trackingRef.current = false;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }

    if (deltaX < 0) {
      onOpenChange(true);
    } else {
      onOpenChange(false);
    }
  };

  const memberInfo = (
    <>
      {memberProfile?.profile_photo_url ? (
        <Button
          type="button"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            if (isOpen) {
              onOpenChange(false);
              return;
            }
            onMemberPhotoZoom({
              url: memberProfile.profile_photo_url,
              alt: memberProfile?.display_name || 'Member',
            });
          }}
          className="shrink-0 !h-auto rounded-full !p-0 hover:bg-transparent focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Avatar
            displayName={memberProfile?.display_name}
            profilePhotoUrl={memberProfile.profile_photo_url}
            size="sm"
          />
        </Button>
      ) : (
        <div className="shrink-0">
          <Avatar
            displayName={memberProfile?.display_name}
            profilePhotoUrl={memberProfile?.profile_photo_url}
            size="sm"
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            onOpenChange(false);
            return;
          }
          if (!isCurrentUser) {
            onOpenMemberProfile(member.user_id);
          }
        }}
        className="min-w-0 flex-1 cursor-pointer rounded-lg border-0 bg-transparent p-0 text-left hover:bg-black/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-white/5"
      >
        <div className="flex min-w-0 items-center justify-start gap-1 text-left text-sm font-medium text-gray-900 dark:text-dark-text">
          {(member.role === 'creator' || member.role === 'manager') && (
            <Crown
              className="h-3.5 w-3.5 shrink-0 text-primary"
              aria-label={member.role === 'creator' ? 'Creator' : 'Manager'}
            />
          )}
          <span className="truncate">
            {memberProfile?.display_name || 'Unknown'}
            {isCurrentUser && ' (You)'}
            {isBlocked && ' (Blocked)'}
          </span>
        </div>
        <div className="text-left text-xs text-gray-500 dark:text-dark-muted">
          Joined {formatDate(member.joined_at)}
        </div>
      </button>
      <DayDots
        days={festivalDays}
        attendingDays={attendingDays}
        viewerAttendingDays={viewerAttendingDays}
        emptyLabel="No days yet"
      />
    </>
  );

  return (
    <div
      ref={rowRef}
      className={`relative overflow-hidden touch-pan-y ${isBlocked ? 'opacity-60' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {actionKeys.length === 0 ? (
        <div className="flex items-center gap-3 px-2 py-2 hover:bg-black/[0.03] dark:hover:bg-white/5 transition-colors">
          {memberInfo}
        </div>
      ) : (
        <>
          <div
            className={`relative z-0 flex w-full min-w-0 items-center transition-transform duration-250 ease-out ${rowSlideClass}`}
          >
            <div className="flex min-w-0 flex-1 items-center gap-3 px-2 py-2">{memberInfo}</div>
          </div>
          <div
            data-mobile-member-actions="true"
            className={`absolute inset-y-0 right-0 z-10 flex shrink-0 flex-nowrap items-center justify-end gap-1 px-2 transition-transform duration-250 ease-out ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            } ${strip}`}
          >
            {actionKeys.includes('leave') && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenChange(false);
                  onLeaveGroup();
                }}
                className="shrink-0"
              >
                Leave Group
              </Button>
            )}
            {actionKeys.includes('promote') && (
              <Button
                type="button"
                size="sm"
                variant="primary"
                onClick={async (event) => {
                  event.stopPropagation();
                  await onPromoteMember(member);
                  onOpenChange(false);
                }}
                className="shrink-0"
              >
                Promote
              </Button>
            )}
            {actionKeys.includes('remove') && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemoveMember(member);
                  onOpenChange(false);
                }}
                className="shrink-0"
              >
                Remove
              </Button>
            )}
            {actionKeys.includes('unblock') && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={async (event) => {
                  event.stopPropagation();
                  await onUnblockMember(member.user_id);
                  onOpenChange(false);
                }}
                className="shrink-0"
              >
                Unblock
              </Button>
            )}
            {actionKeys.includes('block') && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  onBlockMember(member.user_id);
                  onOpenChange(false);
                }}
                className="shrink-0"
              >
                Block
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
